<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useDevMode } from '@/composables/useDevMode'
import { useJwtStore } from '@/stores'

const route = useRoute()
const jwtStore = useJwtStore()
const { devMode, isDebugOverlayVisible, simulatedRole, getEffectiveRole } = useDevMode()

// Viewport dimensions
const viewport = ref({ width: 0, height: 0 })

// Update viewport dimensions
const updateViewport = () => {
  viewport.value = {
    width: window.innerWidth,
    height: window.innerHeight
  }
}

// Get breakpoint name
const breakpoint = computed(() => {
  const w = viewport.value.width
  if (w < 600) return 'xs'
  if (w < 960) return 'sm'
  if (w < 1280) return 'md'
  if (w < 1920) return 'lg'
  return 'xl'
})

// JWT info
const jwt = computed(() => jwtStore.getJwtContent)
const effectiveRole = computed(() => getEffectiveRole(jwt.value?.role))

// Route info
const routeInfo = computed(() => ({
  name: route.name,
  path: route.path,
  params: Object.keys(route.params).length > 0 ? route.params : null,
  query: Object.keys(route.query).length > 0 ? route.query : null,
  meta: route.meta
}))

// Memory usage (if available)
const memoryUsage = ref<{ used: string; total: string } | null>(null)

const updateMemory = () => {
  if ('memory' in performance) {
    const memory = (performance as { memory: { usedJSHeapSize: number; totalJSHeapSize: number } }).memory
    memoryUsage.value = {
      used: (memory.usedJSHeapSize / 1024 / 1024).toFixed(1),
      total: (memory.totalJSHeapSize / 1024 / 1024).toFixed(1)
    }
  }
}

let memoryInterval: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  updateViewport()
  updateMemory()
  window.addEventListener('resize', updateViewport)
  memoryInterval = setInterval(updateMemory, 2000)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateViewport)
  if (memoryInterval) clearInterval(memoryInterval)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="overlay">
      <div v-if="devMode && isDebugOverlayVisible" class="debug-overlay">
        <!-- Viewport Badge -->
        <div class="debug-badge viewport-badge">
          <v-icon icon="mdi-monitor-screenshot" size="12" />
          <span class="badge-value">{{ viewport.width }}×{{ viewport.height }}</span>
          <span class="badge-breakpoint">{{ breakpoint }}</span>
        </div>

        <!-- Route Badge -->
        <div class="debug-badge route-badge">
          <v-icon icon="mdi-routes" size="12" />
          <span class="badge-value">{{ routeInfo.name || routeInfo.path }}</span>
        </div>

        <!-- Role Badge -->
        <div class="debug-badge role-badge" :class="`role-${effectiveRole}`">
          <v-icon icon="mdi-account-circle" size="12" />
          <span class="badge-value">{{ effectiveRole }}</span>
          <span v-if="simulatedRole" class="badge-sim">(sim)</span>
        </div>

        <!-- Memory Badge (if available) -->
        <div v-if="memoryUsage" class="debug-badge memory-badge">
          <v-icon icon="mdi-memory" size="12" />
          <span class="badge-value">{{ memoryUsage.used }}MB</span>
        </div>

        <!-- Route Details Panel -->
        <div class="debug-details">
          <div class="details-section">
            <span class="details-label">Route</span>
            <code class="details-value">{{ routeInfo.path }}</code>
          </div>
          <div v-if="routeInfo.params" class="details-section">
            <span class="details-label">Params</span>
            <code class="details-value">{{ JSON.stringify(routeInfo.params) }}</code>
          </div>
          <div v-if="routeInfo.query" class="details-section">
            <span class="details-label">Query</span>
            <code class="details-value">{{ JSON.stringify(routeInfo.query) }}</code>
          </div>
          <div v-if="jwt" class="details-section">
            <span class="details-label">User</span>
            <code class="details-value">{{ jwt.email || jwt.name || 'N/A' }} (ID: {{ jwt.id }})</code>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.debug-overlay {
  position: fixed;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9998;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background: rgba(20, 10, 30, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(156, 39, 176, 0.3);
  border-radius: 10px;
  font-family: monospace;
  font-size: 11px;
}

.debug-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.8);

  .v-icon {
    color: rgba(206, 147, 216, 0.7);
  }
}

.badge-value {
  color: rgba(255, 255, 255, 0.9);
}

.badge-breakpoint {
  padding: 1px 4px;
  background: rgba(156, 39, 176, 0.3);
  border-radius: 3px;
  font-size: 9px;
  font-weight: 600;
  color: #CE93D8;
  text-transform: uppercase;
}

.badge-sim {
  font-size: 9px;
  color: rgba(255, 193, 7, 0.8);
}

.role-badge {
  &.role-admin {
    background: rgba(156, 39, 176, 0.15);
    .badge-value {
      color: #CE93D8;
    }
  }

  &.role-reseller {
    background: rgba(31, 226, 212, 0.15);
    .badge-value {
      color: #1FE2D4;
    }
  }

  &.role-customer {
    background: rgba(0, 176, 255, 0.15);
    .badge-value {
      color: #00B0FF;
    }
  }
}

.memory-badge {
  .badge-value {
    color: rgba(76, 175, 80, 0.9);
  }
}

.debug-details {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  padding: 8px 10px;
  background: rgba(20, 10, 30, 0.95);
  border: 1px solid rgba(156, 39, 176, 0.2);
  border-radius: 8px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

.debug-overlay:hover .debug-details {
  opacity: 1;
  pointer-events: auto;
}

.details-section {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
}

.details-label {
  flex-shrink: 0;
  font-size: 9px;
  font-weight: 600;
  color: rgba(206, 147, 216, 0.6);
  text-transform: uppercase;
  min-width: 50px;
}

.details-value {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.8);
  word-break: break-all;
}

/* Overlay transition */
.overlay-enter-active,
.overlay-leave-active {
  transition: all 0.2s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-10px);
}

@media (max-width: 600px) {
  .debug-overlay {
    flex-wrap: wrap;
    max-width: calc(100vw - 16px);
    justify-content: center;
  }

  .debug-badge {
    font-size: 10px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .debug-details {
    transition: none;
  }

  .overlay-enter-active,
  .overlay-leave-active {
    transition: opacity 0.1s ease;
  }

  .overlay-enter-from,
  .overlay-leave-to {
    transform: translateX(-50%);
  }
}
</style>
